import React, { FC, useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Flex, Grid } from '$components/layouts';
import { formatString, mq } from '$lib/helpers';
import { useIntersectionObserver, useTranslation } from '$hooks';
import { Loading } from '$components/elements/loading/loading';
import { ProductCard } from '$components/elements/product-card';
import { Button } from '$components/elements/button';
import { IMappedHelloRetailProduct } from '~/modules/search/interfaces/suggestion';

type Props = {
    loadingPage?: boolean;
    onLoadMore?: () => void;
    products?: IMappedHelloRetailProduct[];
    totalResults?: number;
    triggerInifinityScroll?: boolean;
    userInteracted?: boolean;
};

export const ProductGrid: FC<Props> = ({
    loadingPage,
    products,
    onLoadMore,
    totalResults = 0,
    triggerInifinityScroll = true,
    userInteracted,
}: Props) => {
    const [infScrollEnabled, setInfScrollEnabled] = useState(!triggerInifinityScroll);
    const theEnd = useRef<HTMLDivElement>(null);
    const { translate } = useTranslation();
    const intersection = useIntersectionObserver(theEnd);

    const minimumShown = 12;

    useEffect(() => {
        if (infScrollEnabled && intersection?.isIntersecting && totalResults > (products?.length || 0)) {
            onLoadMore && onLoadMore();
        }
    }, [intersection?.isIntersecting]);

    useEffect(() => {
        if (infScrollEnabled && !userInteracted) {
            setInfScrollEnabled(false);
        }
    }, [userInteracted, infScrollEnabled]);

    return (
        <>
            <ProductListGrid
                flow="row"
                columns="repeat(2, minmax(0, 1fr))"
                columnGap="small"
                rowGap="small"
                breakpoints={{
                    [mq('md')]: {
                        columns: 'repeat(3, minmax(0, 1fr))',
                    },
                    [mq('ml')]: {
                        columns: 'repeat(4, minmax(0, 1fr))',
                    },
                    [mq('lg')]: {
                        columns: 'repeat(5, minmax(0, 1fr))',
                    },
                }}
            >
                {/* remove index from key */}
                {products?.map((p, i) => (
                    <ProductCard
                        eagerLoad={i < minimumShown}
                        key={`${p.productNumber}-${i}`}
                        {...p}
                        forcedSpecialPrice={p?.isSpecialPrice}
                    />
                ))}
            </ProductListGrid>
            <div ref={theEnd} />
            <ProductListActions justifyContent="center" alignItems="center">
                {loadingPage && <Loading />}
                {!infScrollEnabled && !!products?.length && products?.length < totalResults && (
                    <Button
                        onClick={() => {
                            setInfScrollEnabled(true);
                            onLoadMore?.();
                        }}
                    >
                        {formatString(translate('search.filterAndFacets.loadMore'), totalResults)}
                    </Button>
                )}
            </ProductListActions>
        </>
    );
};

const ProductListActions = styled(Flex)(({ theme }) => ({
    width: '100%',
    margin: `${theme.space[7]} 0`,
}));

const ProductListGrid = styled(Grid)({
    width: '100%',
});
